
import * as React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

type Theme = "dark" | "light" | "system";

const prefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  actualTheme: "dark" | "light";
};

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  actualTheme: "light",
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

/**
 * Behtari is light-only on every public/auth surface (memory:
 * public-surface-light-mode-enforcement). Dark mode is permitted ONLY under
 * authenticated `/dashboard/*` routes, gated by the `isDashboard` check below.
 * Public routes (`/`, `/login`, `/signup`, `/persona-selection`, etc.) always
 * resolve to "light" regardless of the user's saved preference.
 *
 * The provider is route-aware: it reads `useLocation()` (it is mounted inside
 * BrowserRouter) and re-resolves the document root class on theme OR route
 * change. `theme` is the user's intent ('light'|'dark'|'system'); `actualTheme`
 * is the resolved value actually applied to the DOM.
 */
export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "behtari-theme",
  ...props
}: ThemeProviderProps) {
  const location = useLocation();
  const [theme, setTheme] = useState<Theme>("light");
  // Tracks OS-level color-scheme so `system` mode reacts to live changes.
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(prefersDark);

  // React to OS color-scheme changes while in `system` mode.
  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const isDashboard = location.pathname.startsWith("/dashboard");

  // Resolve the effective theme: dark is only ever allowed under /dashboard/*.
  const resolvedDark =
    isDashboard &&
    (theme === "dark" || (theme === "system" && systemPrefersDark));
  const actualTheme: "dark" | "light" = resolvedDark ? "dark" : "light";

  // Apply the resolved theme to the document root on theme OR route change.
  useEffect(() => {
    const root = window.document.documentElement;
    if (resolvedDark) {
      root.classList.remove("light");
      root.classList.add("dark");
      root.setAttribute("data-theme", "dark");
    } else {
      root.classList.remove("dark");
      root.classList.add("light");
      root.setAttribute("data-theme", "light");
    }
  }, [theme, location.pathname, resolvedDark]);

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      try {
        localStorage.setItem(storageKey, newTheme);
      } catch (e) {
        console.warn("Failed to save theme to localStorage:", e);
      }
      setTheme(newTheme);
    },
    actualTheme,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
